import { useCallback, useEffect, useState } from 'react';
import { jobsApi } from '@/lib/api';
import type { SavedJob } from '@/lib/types';

type State = {
  jobs: SavedJob[];
  loading: boolean;
  error: string | null;
};

/**
 * Sort to match the backend: starred jobs first, then newest first within each
 * group. Used after optimistic local mutations so the UI reorders immediately.
 */
function sortJobs(jobs: SavedJob[]): SavedJob[] {
  return [...jobs].sort((a, b) => {
    if (a.starred !== b.starred) return a.starred ? -1 : 1;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

export function useJobs() {
  const [state, setState] = useState<State>({ jobs: [], loading: true, error: null });

  const refresh = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await jobsApi.list();
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Request failed (${res.status})`);
      }
      const data = (await res.json()) as { jobs: SavedJob[] };
      setState({ jobs: data.jobs, loading: false, error: null });
    } catch (err) {
      setState({
        jobs: [],
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load jobs',
      });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const remove = useCallback(async (id: string) => {
    const res = await jobsApi.remove(id);
    if (!res.ok) throw new Error(`Delete failed (${res.status})`);
    setState((s) => ({ ...s, jobs: s.jobs.filter((j) => j.id !== id) }));
  }, []);

  // Optimistically flip the star and re-sort; revert on failure.
  const toggleStar = useCallback(async (job: SavedJob) => {
    const nextStarred = !job.starred;
    setState((s) => ({
      ...s,
      jobs: sortJobs(s.jobs.map((j) => (j.id === job.id ? { ...j, starred: nextStarred } : j))),
    }));
    try {
      const res = await jobsApi.setStarred(job.id, nextStarred);
      if (!res.ok) throw new Error(`Update failed (${res.status})`);
    } catch {
      // Revert on error.
      setState((s) => ({
        ...s,
        jobs: sortJobs(s.jobs.map((j) => (j.id === job.id ? { ...j, starred: job.starred } : j))),
      }));
      throw new Error('Could not update star');
    }
  }, []);

  return { ...state, refresh, remove, toggleStar };
}